// src/components/TeachingChatbot.tsx
import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Bot, X, Send, Sparkles } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from './ui/sheet';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { askAI } from '../lib/chatAI';

/* ⬇️ Markdown support (safe: no raw HTML) */
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { CHAT_TITLE, CHAT_PLACEHOLDER, CHAT_WELCOME, CHAT_SYSTEM_PROMPT } from '../constants/chat';

/* =========================
 * Helpers
 * =======================*/
const newId = () =>
  (globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`);

/* =========================
 * Types
 * =======================*/
interface Message {
  id: string; // stable unique id to avoid key collisions
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
}

/* Make “almost Markdown” safer to render */
function repairMarkdown(src: string): string {
  let s = src;
  const fenceCount = (s.match(/```/g) || []).length;
  if (fenceCount % 2 === 1) s += '\n```';
  s = s.replace(/^(\s*#{1,6})([^\s#])/gm, (_, hashes, rest) => `${hashes} ${rest}`);
  return s;
}

/* Markdown renderer with fixed small typography (no `prose`) */
function MarkdownMessage({ content }: { content: string }) {
  const safe = repairMarkdown(content);
  return (
    <div
      className="
        text-sm leading-5
        [&_p]:mb-3 [&_p]:last:mb-0
        [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1
        [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1
        [&_li]:text-sm
        [&_strong]:font-semibold
        [&_a]:text-cyan-300 [&_a]:no-underline hover:[&_a]:underline
        [&_h1]:text-sm [&_h1]:font-semibold [&_h1]:mt-2 [&_h1]:mb-1
        [&_h2]:text-sm [&_h2]:font-semibold [&_h2]:mt-2 [&_h2]:mb-1
        [&_h3]:text-sm [&_h3]:font-semibold [&_h3]:mt-2 [&_h3]:mb-1
        [&_code]:text-[0.85rem] [&_code]:px-1 [&_code]:py-0.5 [&_code]:bg-white/10 [&_code]:rounded
        [&_pre]:bg-white/5 [&_pre]:border [&_pre]:border-white/10 [&_pre]:rounded-xl
        [&_pre]:p-3 [&_pre]:text-[0.8rem] [&_pre]:overflow-x-auto
      "
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>
        {safe}
      </ReactMarkdown>
    </div>
  );
}

export function TeachingChatbot() {
  // ... your existing state
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: newId(),
      text: CHAT_WELCOME,
      sender: 'bot',
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isTyping, isOpen]);

  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: newId(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date(),
    };
    const userText = inputValue;
    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    try {
      const aiText = await askAI(
        [
          ...messages.map((m) => ({
            role: m.sender === 'user' ? 'user' : 'assistant',
            content: m.text,
          })),
          { role: 'user', content: userText },
        ],
        { system: CHAT_SYSTEM_PROMPT, model: 'gpt-4o-mini' }
      );

      const botResponse: Message = {
        id: newId(),
        text: aiText || "Sorry, I couldn't generate a response.",
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botResponse]);
    } catch (err: any) {
      const botError: Message = {
        id: newId(),
        text:
          'Hmm, I had trouble reaching our AI service. Please try again in a moment.',
        sender: 'bot',
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, botError]);
      console.error('chat-ai error:', err);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // ... the rest of your component JSX stays unchanged
  return (
    <>
      {/* Floating Action Button */}
      <AnimatePresence>
        {!isOpen && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 20 }}
            className="fixed bottom-8 right-8 z-50"
          >
            <motion.button
              onClick={() => setIsOpen(true)}
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              className="group relative w-16 h-16 rounded-full bg-gradient-to-r from-[#00BFFF] to-[#8A2BE2] shadow-lg shadow-[#00BFFF]/50 hover:shadow-xl hover:shadow-[#00BFFF]/70 transition-shadow"
            >
              {/* Pulse effect */}
              <motion.div
                className="absolute inset-0 rounded-full bg-gradient-to-r from-[#00BFFF] to-[#8A2BE2]"
                animate={{
                  scale: [1, 1.3, 1],
                  opacity: [0.5, 0, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              />

              {/* Icon */}
              <div className="relative flex items-center justify-center h-full">
                <Bot className="w-8 h-8 text-white" />
              </div>

              {/* Sparkle indicator */}
              <motion.div
                className="absolute -top-1 -right-1"
                animate={{
                  rotate: [0, 360],
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: 'linear',
                }}
              >
                <Sparkles className="w-5 h-5 text-yellow-300" />
              </motion.div>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Chat Sheet */}
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetContent
          side="right"
          forceMount
          className="w-full sm:max-w-md p-0 bg-[#050B1A]/95 backdrop-blur-xl border-l border-white/10"
        >
          <div className="flex flex-col h-full min-h-0">
            {/* Header */}
            <SheetHeader className="p-6 border-b border-white/10 bg-gradient-to-r from-[#00BFFF]/10 to-[#8A2BE2]/10">
              <div className="flex items-center gap-3">
                <motion.div
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 20, repeat: Infinity, ease: 'linear' }}
                  className="w-12 h-12 rounded-full bg-gradient-to-r from-[#00BFFF] to-[#8A2BE2] flex items-center justify-center shadow-lg shadow-[#00BFFF]/50"
                >
                  <Bot className="w-6 h-6 text-white" />
                </motion.div>
                <div className="flex-1">
                  <SheetTitle className="text-white">{CHAT_TITLE}</SheetTitle>
                  <SheetDescription className="sr-only">
                    Chat assistant drawer. Type your question and press Enter to send.
                  </SheetDescription>
                  <div className="flex items-center gap-2 mt-1">
                    <motion.div
                      animate={{ scale: [1, 1.2, 1], opacity: [1, 0.5, 1] }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="w-2 h-2 rounded-full bg-green-400"
                    />
                    <span className="text-xs text-white/60">Online & ready to help</span>
                  </div>
                </div>
              </div>
            </SheetHeader>

            {/* Messages */}
            <ScrollArea className="flex-1 min-h-0">
              <div className="p-6 space-y-4">
                {messages.map((message) => (
                  <motion.div
                    key={message.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3 }}
                    className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
<div
  className={`max-w-[80%] rounded-2xl px-4 py-3
    text-[13px] leading-5 break-words
    ${message.sender === 'user'
      ? 'whitespace-pre-wrap'
      : 'whitespace-normal'}
    [&_*]:leading-5
    [&_p]:!text-[13px] [&_li]:!text-[13px] [&_h1]:!text-[13px] [&_h2]:!text-[13px] [&_h3]:!text-[13px]
    [&_code]:text-[12px] [&_pre]:text-[12px]
    ${message.sender === 'user'
      ? 'bg-gradient-to-r from-[#00BFFF] to-[#8A2BE2] text-white'
      : 'bg-white/10 text-white border border-white/10'}
  `}
>
                      {message.sender === 'bot' ? (
                        <MarkdownMessage content={message.text} />
                      ) : (
                        <p>{message.text}</p>
                      )}

                      <span className="text-xs opacity-50 mt-1 block">
                        {message.timestamp.toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    </div>
                  </motion.div>
                ))}
                
                {/* Typing Indicator */}
                {isTyping && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex justify-start"
                  >
                    <div className="bg-white/10 border border-white/10 rounded-2xl px-4 py-3">
                      <div className="flex gap-1">
                        {[0, 1, 2].map((i) => (
                          <motion.div
                            key={i}
                            className="w-2 h-2 rounded-full bg-[#00BFFF]"
                            animate={{ y: [0, -8, 0] }}
                            transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.2 }}
                          />
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}
                <div ref={bottomRef} />
              </div>
            </ScrollArea>

            {/* Input */}
            <div className="p-4 border-t border-white/10 bg-[#050B1A]/50">
              <div className="flex gap-2">
                <Input
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={CHAT_PLACEHOLDER}
                  className="flex-1 bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-[#00BFFF] focus:ring-[#00BFFF]"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || isTyping}
                  className="bg-gradient-to-r from-[#00BFFF] to-[#8A2BE2] hover:shadow-lg hover:shadow-[#00BFFF]/50"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              <p className="text-xs text-white/40 mt-2 text-center">
                Ask me about qubits, gates, algorithms, or our courses
              </p>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
